import { createAsyncThunk } from '@reduxjs/toolkit'

import { SeatBasedLicensesResponse } from '@lyrahealth-inc/shared-app-logic/src/features/managerHub/managerCoachingSeatEligibility'
import { CreateChildUser } from '@lyrahealth-inc/shared-app-logic/src/models/searchForCare/SearchForCare'
import {
  EmployeeGroupResponse,
  PutUserPropertyRequest,
  PutUserPropertyResponse,
  User,
  UserNotificationItem,
} from '@lyrahealth-inc/shared-app-logic/src/models/user/User'

import { getId, getUserSeatBasedLicensesLoading } from './userSelectors'
import { noOpActionErrorHandler } from '../../common/http/actionErrorHandler'
import { axiosInstance } from '../../common/http/axiosInstance'
import {
  submitPostMigrationConsent as submitPostMigrationConsentApi,
  updateNotificationItem,
} from '../../common/http/data/editProfile'

export const SET_USER_INFO = 'SET_USER_INFO'
export const setUserInfo = (data: $TSFixMe) => ({
  type: SET_USER_INFO,
  data,
})
export const TOGGLE_OFF_MFA = 'TOGGLE_OFF_MFA'
export const TOGGLE_ON_MFA = 'TOGGLE_ON_MFA'
export const SET_USER_PHONE_NUMBER = 'SET_USER_PHONE_NUMBER'
export const SET_USER_DIRECT_DEDICATED_CARE_NAVIGATOR_LINK = 'SET_USER_DIRECT_DEDICATED_CARE_NAVIGATOR_LINK'
export const SET_USER_CHILDREN = 'SET_USER_CHILDREN'
export const SET_USER_REMAINING_TIME = 'SET_USER_REMAINING_TIME'
export const SET_HAS_PAST_FAVORITED_PROVIDERS = 'SET_HAS_PAST_FAVORITED_PROVIDERS'

export const POST_GUARDIAN_INDICATED = 'POST_GUARDIAN_INDICATED'

export const SET_USER_DEDICATED_CARE_NAVIGATOR_INFO = 'SET_USER_DEDICATED_CARE_NAVIGATOR_INFO'

export const POST_GOOGLE_CRITICAL_TALENT_INFO = 'POST_GOOGLE_CRITICAL_TALENT_INFO'
export const SET_GOOGLE_CRITICAL_TALENT_INFO = 'SET_GOOGLE_CRITICAL_TALENT_INFO'

export const GET_EMPLOYEE_GROUP = 'GET_EMPLOYEE_GROUP'

export const getSeatBasedLicenses = createAsyncThunk<SeatBasedLicensesResponse, string>(
  'GET_SEAT_BASED_LICENSES',
  async (lyraId) => {
    const response = await axiosInstance.get(`/v1/eligibility/seat-based-licenses/${lyraId}`)
    return response.data
  },
  {
    // Pending is dispatched synchronously, so concurrent consumers share one request.
    condition: (_lyraId, { getState }) => !getUserSeatBasedLicensesLoading(getState() as $TSFixMe),
  },
)

export const getDirectDedicatedCareNavigatorLink = (lyraId: string) => ({
  action: SET_USER_DIRECT_DEDICATED_CARE_NAVIGATOR_LINK,
  request: {
    method: 'get',
    url: `/v1/user/${lyraId}/direct-dedicated-care-navigator-link`,
  },
  errorHandler: noOpActionErrorHandler,
})

export const fetchUserChildren = (lyraId: string) => ({
  action: SET_USER_CHILDREN,
  request: {
    method: 'get',
    url: `/v1/user/${lyraId}/child`,
  },
  errorHandler: noOpActionErrorHandler,
})

export const createChildUser = ({ guardianLyraId, data }: CreateChildUser) => {
  return async () => {
    try {
      const response = await axiosInstance.put(`/v1/user/${guardianLyraId}/child`, data)
      return { payload: response.data }
    } catch (error: any) {
      return {
        meta: { requestStatus: 'rejected' },
        payload: {
          data: error.response.data,
        },
      }
    }
  }
}

export const getUserRemainingTime = () => ({
  action: SET_USER_REMAINING_TIME,
  request: {
    method: 'get',
    url: '/unsecured/v1/utils/user-session-remaining-time',
  },
  errorHandler: noOpActionErrorHandler,
})

export const postGuardianIndicated = (lyraId: string) => ({
  action: POST_GUARDIAN_INDICATED,
  request: {
    method: 'post',
    url: `/v1/user/${lyraId}/guardianship/indicated`,
  },
})

export const getDirectDedicatedCareNavigatorInfo = (lyraId: string) => ({
  action: SET_USER_DEDICATED_CARE_NAVIGATOR_INFO,
  request: {
    method: 'get',
    url: `/v1/user/${lyraId}/dedicated-care-navigator`,
  },
  errorHandler: noOpActionErrorHandler,
})

export const updateUserNotificationItem = createAsyncThunk<User, UserNotificationItem>(
  'UPDATE_USER_NOTIFICATION_ITEM',
  async (data, { getState, rejectWithValue }) => {
    const state: any = getState()
    const lyraId = getId(state)
    if (!lyraId) {
      return rejectWithValue(new Error('No lyraId'))
    }
    try {
      const response = await updateNotificationItem(lyraId, data)
      const updatedUser: User = response.data.user
      return updatedUser
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const submitPostMigrationConsent = createAsyncThunk<User, void>(
  'SUBMIT_POST_MIGRATION_CONSENT',
  async (_, { getState, rejectWithValue }) => {
    const state: any = getState()
    const lyraId = getId(state)
    if (!lyraId) {
      return rejectWithValue(new Error('No lyraId'))
    }
    try {
      const response = await submitPostMigrationConsentApi(lyraId)
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const postGoogleCriticalTalentInfo = createAsyncThunk<void, { isUserGoogleCriticalTalent?: boolean }>(
  POST_GOOGLE_CRITICAL_TALENT_INFO,
  async (data, { getState, rejectWithValue }) => {
    const state: any = getState()
    const lyraId = getId(state)
    if (!lyraId) {
      return rejectWithValue(new Error('No lyraId'))
    }
    try {
      const response = await axiosInstance.post(`/v1/user/${lyraId}/google-critical-talent-info`, data)
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const getGoogleCriticalTalentInfo = createAsyncThunk<{ isUserGoogleCriticalTalent: boolean }, void>(
  SET_GOOGLE_CRITICAL_TALENT_INFO,
  async (_, { getState, rejectWithValue }) => {
    const state: any = getState()
    const lyraId = getId(state)
    if (!lyraId) {
      return rejectWithValue(new Error('No lyraId'))
    }
    try {
      const response = await axiosInstance.get(`/v1/user/${lyraId}/google-critical-talent-info`)
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const getEmployeeGroup = createAsyncThunk<EmployeeGroupResponse, { userLyraId: string }>(
  GET_EMPLOYEE_GROUP,
  async ({ userLyraId }, { rejectWithValue }) => {
    if (!userLyraId) {
      return rejectWithValue(new Error('Missing userLyraId'))
    }
    try {
      const response = await axiosInstance.get(`/v1/user/${userLyraId}/employee-group`)
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const putUserProperty = createAsyncThunk<
  PutUserPropertyResponse,
  { lyraId: string; requestData: PutUserPropertyRequest }
>('PUT_USER_PROPERTY', async ({ lyraId, requestData }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put(`/v1/patient/${lyraId}/property/${requestData.name}`, {
      value: requestData.value,
      classType: requestData.classType,
    })
    return response.data
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const verifyUserMFA = createAsyncThunk<boolean, { lyraId: string; type: string }>(
  'VERIFY_USER_MFA',
  async ({ lyraId, type }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/v1/mfa/verify/${lyraId}`, { type })
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)
